var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createPatch } from 'rfc6902';
import { api } from 'utils/apis/api';
import { transformCreateAttributeArg, transformSelectAttributeOptionsArg, } from './utils/transformArg';
import { transformCreateAttributeResponse, transformReadAttributesResponse, transformUpdateAttributeResponse, } from './utils/transformResponse';
export const customFieldsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        readAttributes: builder.query({
            query: ({ entityId }) => ({
                version: 1,
                path: `custom-fields/schema/${entityId}`,
            }),
            transformResponse: transformReadAttributesResponse,
        }),
        createAttribute: builder.mutation({
            query: ({ entityId, attribute }) => ({
                version: 1,
                path: `custom-fields/schema/${entityId}`,
                method: 'POST',
                body: transformCreateAttributeArg(attribute),
            }),
            transformResponse: transformCreateAttributeResponse,
            onQueryStarted({ entityId }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    const response = yield queryFulfilled;
                    dispatch(customFieldsApi.util.updateQueryData('readAttributes', { entityId }, (draft) => {
                        const attributes = draft.data.attributes;
                        attributes.push(response.data.data);
                    }));
                });
            },
        }),
        updateAttribute: builder.mutation({
            query: ({ entityId, attributeId, patch: { before, after } }) => ({
                version: 1,
                path: `custom-fields/schema/${entityId}/${attributeId}`,
                method: 'PATCH',
                body: createPatch(Object.assign({}, before, before.type === 'select'
                    ? { options: transformSelectAttributeOptionsArg(before.options) }
                    : {}), Object.assign({}, after, after.type === 'select'
                    ? { options: transformSelectAttributeOptionsArg(after.options) }
                    : {})),
            }),
            transformResponse: transformUpdateAttributeResponse,
            onQueryStarted({ entityId, attributeId, patch: { after } }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    const patchResult = dispatch(customFieldsApi.util.updateQueryData('readAttributes', { entityId }, (draft) => {
                        const attributes = draft.data.attributes;
                        const attributeIndex = draft.data.attributes.findIndex(({ id }) => id === attributeId);
                        if (attributeIndex !== -1) {
                            attributes[attributeIndex] = Object.assign({}, attributes[attributeIndex], after);
                        }
                        attributes.sort((a, b) => a.order_index - b.order_index);
                    }));
                    try {
                        yield queryFulfilled;
                    }
                    catch (_a) {
                        patchResult.undo();
                    }
                });
            },
        }),
        updateAttributeArchiveState: builder.mutation({
            query: ({ entityId, attributeId, isArchived }) => ({
                version: 1,
                path: `custom-fields/schema/${entityId}/${attributeId}/archive`,
                method: isArchived ? 'POST' : 'DELETE',
            }),
            onQueryStarted({ entityId, attributeId, isArchived }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    const patchResult = dispatch(customFieldsApi.util.updateQueryData('readAttributes', { entityId }, (draft) => {
                        const attributes = draft.data.attributes;
                        const attributeIndex = draft.data.attributes.findIndex(({ id }) => id === attributeId);
                        if (attributeIndex !== -1) {
                            attributes[attributeIndex].is_archived = isArchived;
                        }
                    }));
                    try {
                        yield queryFulfilled;
                    }
                    catch (_a) {
                        patchResult.undo();
                    }
                });
            },
        }),
        readValue: builder.query({
            query: ({ entityId, entityInstanceId }) => ({
                version: 1,
                path: `custom-fields/data/${entityId}/${entityInstanceId}`,
            }),
        }),
        updateValue: builder.mutation({
            query: ({ entityId, entityInstanceId, patch }) => ({
                version: 1,
                path: `custom-fields/data/${entityId}/${entityInstanceId}`,
                method: 'PATCH',
                body: createPatch({
                    attribute_values: patch.before,
                }, {
                    attribute_values: patch.after,
                }),
            }),
            onQueryStarted({ entityId, entityInstanceId, patch: { after, before } }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    const patchResult = dispatch(customFieldsApi.util.updateQueryData('readValue', { entityId, entityInstanceId }, (draft) => {
                        if (draft === null || draft === void 0 ? void 0 : draft.data) {
                            const changedValuesKeys = [
                                ...Object.keys(before),
                                ...Object.keys(after),
                            ];
                            changedValuesKeys.forEach((key) => {
                                draft.data.attribute_values[key] = after[key];
                            });
                        }
                    }));
                    try {
                        yield queryFulfilled;
                    }
                    catch (_a) {
                        patchResult.undo();
                    }
                });
            },
        }),
        readVisibility: builder.query({
            query: ({ entityId, context, params }) => ({
                version: 1,
                method: 'POST',
                path: `custom-fields/visibility/${entityId}`,
                body: context,
                params,
            }),
        }),
    }),
});
